import React, { useCallback, useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Divider,
  Paper,
  Card,
  CardContent,
  Checkbox,
  Stack,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../auth';
import { LoadingPane, PageHeader } from '../components/page-elements';

const UpdateCustomers = () => {
  const { id } = useParams(); // Assumes a route like /customers/update/:id
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    customerCode: '',
    customerAddress1: '',
    customerAddress2: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    customerLogo: '',
    loadGoalMinutes: 90,
    enableGroundInventory: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [newUserForm, setNewUserForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'customer',
    receiveBols: false,
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState('');
  const [createUserSuccess, setCreateUserSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'customer',
    receiveBols: false,
  });
  const [editingBusy, setEditingBusy] = useState(false);
  const initialLoading = loading && !formData.customerName && !error;

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${id}/users`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to fetch associated users');
      }
      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingUsers(false);
    }
  }, [id]);

  // Fetch existing customer data.
  useEffect(() => {
    const fetchCustomer = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch customer data');
        }
        const data = await response.json();
        setFormData({
          customerName: data.customerName || '',
          customerCode: data.customerCode || '',
          customerAddress1: data.customerAddress1 || '',
          customerAddress2: data.customerAddress2 || '',
          customerCity: data.customerCity || '',
          customerState: data.customerState || '',
          customerZip: data.customerZip || '',
          customerLogo: data.customerLogo || '',
          loadGoalMinutes: Number.isFinite(Number(data.loadGoalMinutes)) ? Number(data.loadGoalMinutes) : 90,
          enableGroundInventory: Boolean(data.enableGroundInventory),
        });
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };

    fetchCustomer();
    fetchUsers();
  }, [id, fetchUsers]);

  // Handle text field changes.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle logo file upload.
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          customerLogo: reader.result, // Store as base64 string.
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit handler to update the customer.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        ...formData,
        loadGoalMinutes: Number.isFinite(Number(formData.loadGoalMinutes))
          ? Number(formData.loadGoalMinutes)
          : formData.loadGoalMinutes,
        enableGroundInventory: Boolean(formData.enableGroundInventory),
      };
      const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || data?.errors?.[0]?.msg || 'Failed to update customer');
      }
      setSuccess('Customer updated successfully!');
      // Optionally navigate back to the customer list.
      navigate('/customers');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    setCreateUserError('');
    setCreateUserSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...newUserForm,
          customerName: id,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user');
      }

      setCreateUserSuccess('User created successfully.');
      setNewUserForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'customer',
        receiveBols: false,
      });
      await fetchUsers();
    } catch (err) {
      setCreateUserError(err.message);
    }

    setCreatingUser(false);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'customer',
      receiveBols: Boolean(user.receiveBols),
    });
    setEditOpen(true);
  };

  const closeEditUser = () => {
    setEditOpen(false);
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      email: '',
      role: 'customer',
      receiveBols: false,
    });
  };

  const handleEditUserChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveUserEdit = async () => {
    if (!editingUser?._id) return;
    setEditingBusy(true);
    setCreateUserError('');
    setCreateUserSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${editingUser._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          email: editForm.email,
          role: editForm.role,
          receiveBols: Boolean(editForm.receiveBols),
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to update user');
      }

      closeEditUser();
      await fetchUsers();
      setCreateUserSuccess('User updated successfully.');
    } catch (err) {
      setCreateUserError(err.message);
    }

    setEditingBusy(false);
  };

  const deactivateUser = async (user) => {
    if (!user?._id) return;
    setCreateUserError('');
    setCreateUserSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user._id}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ isActive: false }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to deactivate user');
      }

      await fetchUsers();
      setCreateUserSuccess('User deactivated successfully.');
    } catch (err) {
      setCreateUserError(err.message);
    }
  };

  return (
    <Box p={2}>
      <PageHeader
        title={`Update Customer${formData.customerName ? `: ${formData.customerName}` : ''}`}
        subtitle="Maintain customer profile, branding, and associated users."
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate(`/customers/customer/${id}`)}>
              Back to Profile
            </Button>
          </>
        }
      />

      {initialLoading ? <LoadingPane label="Loading customer..." /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

      {!initialLoading ? (
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                Company Profile
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={8}>
                    <TextField
                      label="Company Name"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Customer Code"
                      name="customerCode"
                      value={formData.customerCode}
                      onChange={handleChange}
                      fullWidth
                      helperText="Optional. Use for external railcar extract matching."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={Boolean(formData.enableGroundInventory)}
                          onChange={(e) => setFormData((prev) => ({ ...prev, enableGroundInventory: Boolean(e.target.checked) }))}
                        />
                      )}
                      label="Enable Ground Inventory Conversion and Allocation"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Company Address 1"
                      name="customerAddress1"
                      value={formData.customerAddress1}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Company Address 2"
                      name="customerAddress2"
                      value={formData.customerAddress2}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Company City"
                      name="customerCity"
                      value={formData.customerCity}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Company State"
                      name="customerState"
                      value={formData.customerState}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Company ZIP"
                      name="customerZip"
                      value={formData.customerZip}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Load Goal (minutes)"
                      name="loadGoalMinutes"
                      type="number"
                      value={formData.loadGoalMinutes}
                      onChange={handleChange}
                      fullWidth
                      inputProps={{ min: 1, max: 180, step: 1 }}
                      helperText="Dashboard average time-to-load target."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                      <Button variant="outlined" component="label">
                        Upload Logo
                        <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                      </Button>
                      {logoFile ? <Typography variant="body2">{logoFile.name}</Typography> : null}
                    </Stack>
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? 'Updating...' : 'Save Customer Changes'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Logo Preview
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {formData.customerLogo ? (
                <Box sx={{ p: 1.5, border: '1px dashed #cbd5e1', borderRadius: 2, textAlign: 'center', backgroundColor: '#ffffff' }}>
                  <img src={formData.customerLogo} alt="Customer Logo" style={{ maxWidth: '100%', maxHeight: '140px' }} />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No logo uploaded.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      ) : null}

      <Box mt={5}>
        <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2, mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Associated Users
            </Typography>
            {loadingUsers ? (
              <Typography>Loading users...</Typography>
            ) : users.length === 0 ? (
              <Typography>No users found for this customer.</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ mb: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Receive BOLs</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>{user.fullName || `${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role || 'N/A'}</TableCell>
                        <TableCell>{user.receiveBols ? 'Yes' : 'No'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.isActive === false ? 'Inactive' : 'Active'}
                            color={user.isActive === false ? 'default' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit user">
                            <span>
                              <IconButton color="primary" onClick={() => openEditUser(user)}>
                                <EditIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Deactivate user">
                            <span>
                              <IconButton
                                color="warning"
                                onClick={() => deactivateUser(user)}
                                disabled={user.isActive === false}
                              >
                                <BlockIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined" className="surface-hover" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Register New User For This Customer
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {createUserError ? <Alert severity="error" sx={{ mb: 1 }}>{createUserError}</Alert> : null}
            {createUserSuccess ? <Alert severity="success" sx={{ mb: 1 }}>{createUserSuccess}</Alert> : null}
            <Box component="form" onSubmit={handleCreateUser} maxWidth={560}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First Name"
                    name="firstName"
                    value={newUserForm.firstName}
                    onChange={handleNewUserChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last Name"
                    name="lastName"
                    value={newUserForm.lastName}
                    onChange={handleNewUserChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={newUserForm.email}
                    onChange={handleNewUserChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(newUserForm.receiveBols)}
                        onChange={(e) =>
                          setNewUserForm((prev) => ({ ...prev, receiveBols: Boolean(e.target.checked) }))
                        }
                      />
                    }
                    label="Receive completed BOL emails"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    name="password"
                    type="password"
                    value={newUserForm.password}
                    onChange={handleNewUserChange}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
              <Box mt={2}>
                <Button type="submit" variant="contained" disabled={creatingUser}>
                  {creatingUser ? 'Registering...' : 'Register User'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={editOpen} onClose={closeEditUser} fullWidth maxWidth="sm">
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            name="firstName"
            value={editForm.firstName}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={editForm.lastName}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={editForm.email}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Role"
            name="role"
            value={editForm.role}
            onChange={handleEditUserChange}
            fullWidth
            margin="normal"
            select
            required
          >
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(editForm.receiveBols)}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, receiveBols: Boolean(e.target.checked) }))
                }
              />
            }
            label="Receive completed BOL emails"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditUser}>Cancel</Button>
          <Button variant="contained" onClick={saveUserEdit} disabled={editingBusy}>
            {editingBusy ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateCustomers;

